import SellerInboundCurationApiV2 from 'api/v2/admins/seller-inbound-curation-api-v2'
import SellerInboundApiV2 from 'api/v2/admins/seller-inbounds-api-v2'
import SellerProductApiV2 from 'api/v2/admins/seller-product-api-v2'
import RowButton from 'components/atoms/redirect-button'
import TitlePage from 'components/atoms/title-page'
import CardLoader from 'components/organisms/card-loader'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import { ALERT_TYPES } from 'constants/alert-constants'
import { CURATION_STATUS as INBOUND_CURATION_STATUS, LIVE_PROCESS_STATUS } from 'domains/seller-inbound/constants'
import { CURATION_STATUS as PRODUCT_CURATION_STATUS } from 'domains/seller-product/constants'
import { STATUS } from 'domains/seller-product-price-confirmation/constants'
import { PUT_AWAY_STATUSES } from 'domains/seller-inventory-batch/constants'
import SellerInboundCurationCard from 'domains/seller-inbound/organisms/curation-card'
import SellerProductTable from 'domains/seller-product/organisms/table'
import SellerCard from 'domains/seller/organisms/seller-card'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useHistory } from 'react-router'
import { Button, Card, CardFooter, Col, Row, UncontrolledTooltip } from 'reactstrap'
import hiddenColumns from './hidden-columns'
import ConsignmentInboundRequestCard from 'domains/consignment-inbound-request/card/consignment-inbound-request-card'
import SellerInboundCard from 'domains/seller-inbound/organisms/seller-inbound-card/seller-inbound-card'


const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultCnfirmationState = {
  title         : '',
  message       : '',
  isOpen        : false,
  handlerParams : '',
  handler       : () => {}
}

const SellerInboundRequestCardWithLoader  = withLoader(ConsignmentInboundRequestCard, CardLoader)
const SellerInboundCardWithLoader         = withLoader(SellerInboundCard, CardLoader)
const SellerCardWithLoader                = withLoader(SellerCard, CardLoader)
const SellerInboundCurationCardWithLoader = withLoader(SellerInboundCurationCard, CardLoader)

const SellerInboundCurateDetailPage = ({ pageUtils }) => {
  const { id }  = useParams()
  const history = useHistory()

  const [ seller, setSeller ]                 = useState({})
  const [ sellerInbound, setSellerInbound ]   = useState({})
  const [ sellerProducts, setSellerProducts ] = useState([])
  const [ pagination, setPagination ]         = useState({})
  const [ isLoading, setIsLoading ]           = useState(true)

  const [ isRefetchData, setIsRefetchData ] = useState(true)

  const [ isLoadingSellerProducts, setIsLoadingSellerProducts ] = useState(false)
  /**
   * * confirmation object state
   * * provide props for ConfirmationModal
   * * - title: modal title
   * * - message: modal message
   * * - isOpen: to show/hide modal
   * * - handler: handler when pressing 'Okay' button
   * * - handlerParams: handler params
  */
  const [ confirmationState, setConfirmationState ] = useState(defaultCnfirmationState)

  /** Toggle to show/hide confirmation modal */
  const toggleConfirmationModal = useCallback(() => {
    setConfirmationState((prev) => ({
      ...prev,
      isOpen: !prev.isOpen
    }))
  }, [])

  /**
   * Handler to fetch Seller Inbound and its associations
   * @param {number} id - Seller Inbound ID
   */
  const onFetchSellerInbound = useCallback((id) => {
    SellerInboundApiV2.show(id)
      .then((response) => {
        setSellerInbound(response.data.seller_inbound)
        setSeller(response.data.seller_inbound.seller)

        setIsLoading(false)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ pageUtils ])

  /**
   * Handler to fetch collection of Seller Products
   * @param {object} tableState - ReactTable state
   */
  const onFetchSellerProducts = useCallback((tableState) => {
    setIsLoadingSellerProducts(true)
    const additionalFilters = {
      inbound_id: id
    }

    SellerProductApiV2.get({ tableState: tableState, additionalFilters: additionalFilters })
      .then((response) => {
        setSellerProducts(response.data.seller_products)
        setPagination(response.data.meta)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally( () => setIsLoadingSellerProducts(false) )
  }, [ id, pageUtils ])

  /**
   * Handler to delete selected Seller Product
   * @param {number} id - Seller Product ID
   */
  const onDeleteSellerProducts = useCallback((seller_product_id) => {
    SellerInboundCurationApiV2.delete_product({ inbound_id: id, seller_product_id: seller_product_id })
      .then(() => {
        setIsRefetchData(true)
        pageUtils.setAlertMsg('The product has been deleted. Please curate more carefully next time 😄', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => toggleConfirmationModal())
  }, [ id, setIsRefetchData, toggleConfirmationModal, pageUtils ])

  /**
   * Handler to confirm curation process
   * @param {number} id - Seller Product ID
   */
  const onConfirmSellerInboundCuration = useCallback((id) => {
    SellerInboundCurationApiV2.confirm(id)
      .then(() => {
        setIsRefetchData(true)
        pageUtils.setAlertMsg('The inbound has been curated!', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => toggleConfirmationModal())
  }, [ toggleConfirmationModal, pageUtils ])


  /**
   * Edit Seller Product when clicking on the row button
   * @param {obejct} datum - selected Seller Product
   */
  const handleEditProduct = useCallback((datum) => {

    const curationStatus = () => {

      switch (datum.curation_status) {
        case PRODUCT_CURATION_STATUS.ACCEPTED:
          return `curate/accept/${datum.id}`

        case PRODUCT_CURATION_STATUS.REJECTED:
          return `curate/reject/${datum.id}`

        default:
          return ''
      }
    }

    history.push({ pathname: curationStatus() })
  }, [ history ])

  /**
   * Enable edit button in Seller Product if
   * - Accpeted product (curation status is `accepted`)
   *    - put away status is not `complete`
   *    - price confirmation status is not `seller_confirmed
   * - Rejected product (curation status is `rejeceted`)
   */
  const isNotEditable = useCallback((datum) => {

    const {
      curation_status,
      price_confirmation,
      seller_inbound_inventory_batch_item
    } = datum

    if (PRODUCT_CURATION_STATUS.ACCEPTED === curation_status) {

      return seller_inbound_inventory_batch_item?.put_away_status === PUT_AWAY_STATUSES.COMPLETE ||
        price_confirmation.status === STATUS.SELLER_CONFIRMED
    }
    else if (PRODUCT_CURATION_STATUS.REJECTED === curation_status)  {
      // TODO: add condition to disabled if rejected product has been returned to the seller
      return false
    }
  }, [])

  /**
   * Delete Seller Product when clicking on the row button
   * @param {obejct} datum - selected Seller Product
   */
  const handleDeleteProduct = useCallback((datum) => {
    setConfirmationState({
      title         : 'Delete Product',
      message       : 'Are you sure want to delete this product?',
      isOpen        : true,
      handler       : onDeleteSellerProducts,
      handlerParams : datum.id
    })
  }, [ onDeleteSellerProducts ])

  /** Disable button if the curation is finished to curate (already confirmed)  */
  const isCurated = (curation_status) => curation_status === INBOUND_CURATION_STATUS.CURATED


  /** Disable curate button (accept and reject) if the inbound has been lived */
  const isLived = (live_process_Status) => live_process_Status === LIVE_PROCESS_STATUS.COMPLETED

  useEffect(() => {

    if (isRefetchData) {
      // * refetch to get updated data
      // * - Seller Inbound
      // * - Seller Product
      onFetchSellerInbound(id)
      onFetchSellerProducts()

      // ! don't forget to set it to false
      setIsRefetchData(false)
    }
  }, [ id, isRefetchData, onFetchSellerInbound, onFetchSellerProducts ])

  /**
   * row button props for action buttons in the table
   * we use `useMemo` to prevent rerendering
   */
  const rowButtonProps = useMemo(() => [
    {
      header     : 'Edit',
      id         : 'action_edit',
      text       : 'Edit',
      colour     : 'success',
      isDisabled : isNotEditable,
      onClick    : handleEditProduct
    },
    {
      header  : 'Delete',
      id      : 'action_delete',
      text    : 'Delete',
      colour  : 'danger',
      onClick : handleDeleteProduct
    }
  ], [ handleDeleteProduct, handleEditProduct, isNotEditable ])

  const ConfirmButton = () => (
    <Button
      color='success'
      block
      onClick={ () => {
        setConfirmationState({
          title         : `Confirm ${sellerInbound?.curation?.code}`,
          message       : 'Are you sure want to confirm the Curation Process?',
          isOpen        : true,
          handler       : onConfirmSellerInboundCuration,
          handlerParams : id
        })
      } }
      disabled={ isCurated(sellerInbound?.curation_status) }
    >
      { `Confirm ${sellerInbound?.curation?.code}` }
    </Button>
  )

  return (
    <>
      <TitlePage mainTitle={ 'Consignment Inbound' } subTitle={ `Curation ${sellerInbound?.curation?.code}` } />

      <Row>
        <Col>
          <SellerCardWithLoader
            isLoading={ isLoading }
            seller={ seller }
          />

          <SellerInboundRequestCardWithLoader
            isLoading={ isLoading }
            inboundRequest={ sellerInbound?.inbound_request }
            showDetailButton
          />
        </Col>
        <Col>
          <SellerInboundCardWithLoader
            isLoading={ isLoading }
            inbound={ sellerInbound }
          />
        </Col>
        <Col>
          <SellerInboundCurationCardWithLoader
            isLoading={ isLoading }
            inbound={ sellerInbound }
            footerComponent={ <ConfirmButton /> }
          />
        </Col>


      </Row>

      <TitlePage mainTitle={ 'Curated Products' } subTitle={ `Curation ${sellerInbound?.curation?.code}` } />

      <Card>
        <SellerProductTable
          data={ sellerProducts }
          pagination={ pagination }
          onFetchData={ onFetchSellerProducts }
          hiddenColumns={ hiddenColumns }
          isLoading={ isLoadingSellerProducts }
          rowButtonProps={ rowButtonProps }
        />
        <CardFooter className='text-right'>
          <RowButton
            color='success'
            text={ 'Accept Product' }
            path={ 'curate/accept' }
            disabled={ isLived(sellerInbound?.live_process_status) }
            state={{
              seller_type : seller.seller_type,
              gender      : sellerInbound.gender
            }}
          />
          <RowButton
            color='danger'
            text={ 'Reject Product' }
            path={ 'curate/reject' }
            disabled={ isLived(sellerInbound?.live_process_status) }
          />
          <span className='float-right'>
            <div id='seller-inbound-curate'>
              <i className="fas fa-info-circle 3x" />
            </div>
            <UncontrolledTooltip target='seller-inbound-curate'>
              Inbound cannot be recurated if it has already lived
            </UncontrolledTooltip>
          </span>
        </CardFooter>
      </Card>

      <ConfirmationModal
        title={ confirmationState.title }
        isOpen={ confirmationState.isOpen }
        toggle={ toggleConfirmationModal }
        handleConfirmation= { () => confirmationState.handler(confirmationState.handlerParams) }
      >
        { confirmationState.message }
      </ConfirmationModal>
    </>
  )
}

SellerInboundCurateDetailPage.propTypes = propTypes

export default SellerInboundCurateDetailPage
